import firebase from "firebase/compat/app"; //v9
import "firebase/compat/auth"; //v9
import {
  getCache,
  setCache,
  clearAssociatedCaches,
} from "../IndexedDBApi/IDBCache";
import { reservedRequests } from "../../constants";

class CustomQueue {
  constructor(concurrency) {
    this.concurrency = concurrency;
    this.queue = [];
    this.activeCount = 0;
  }

  async add(fn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn, resolve, reject });
      this.next();
    });
  }

  setConcurrency(concurrency) {
    this.concurrency = concurrency;
    this.next();
  }

  next() {
    if (this.activeCount >= this.concurrency || this.queue.length === 0) {
      return;
    }

    const { fn, resolve, reject } = this.queue.shift();
    this.activeCount++;

    fn()
      .then((result) => {
        this.activeCount--;
        resolve(result);
        this.next();
      })
      .catch((error) => {
        this.activeCount--;
        reject(error);
        this.next();
      });
  }
}

const generalQueue = new CustomQueue(4);
const reservedQueue = new CustomQueue(2);

export const Firebase = () => {
  let auth;
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  const getFirebase = async () => {
    const configObj = await (await fetch("/__/firebase/init.json")).json();
    firebase.initializeApp(configObj);
    auth = firebase.auth();
  };

  const doSignInWithEmailAndPassword = (email, password) =>
    auth.signInWithEmailAndPassword(email, password);

  const signInWithGooglePopUp = () => auth.signInWithPopup(provider);
  const signInWithGoogleRedirect = () => auth.signInWithRedirect(provider);

  const doSignOut = () => auth.signOut();

  const signInWithCustomToken = async (token) => {
    await auth.signInWithCustomToken(token);
  };

  const callFn = async (fn, data = {}, cache) => {
    const token = await auth?.currentUser?.getIdToken();
    const headers = {
      "Content-Type": "application/json",
    };
    token && (headers.Authorization = `Bearer ${token}`);

    // If data for this request exists in the cache, and this is a cacheable request,
    // get the data from the cache instead of the network
    if (cache && auth?.currentUser?.uid) {
      try {
        const cached = await getCache(data, auth?.currentUser?.uid, fn);
        if (cached) {
          return cached;
        }
      } catch (e) {
        console.error(e);
      }
    }

    const fetchFn = async () => {
      // Fetching this way bypasses CORS preflight, along with rewrites in firebase.json
      const res = await fetch(`${window.location.origin}/fns/${fn}`, {
        headers,
        method: "post",
        body: JSON.stringify({ data }),
      });
      if (res.status >= 200 && res.status <= 299) {
        const json = await res.json();
        // Cache result if this is a cacheable request
        cache !== undefined &&
          auth?.currentUser?.uid &&
          setCache(data, auth?.currentUser?.uid, fn, json);
        // Delete associated caches if necessary
        clearAssociatedCaches(fn);
        return json;
      }
      throw new Error(res.statusText);
    };

    // Reserved requests (SRC endpoints) will be able to be called ASAP. Once they're finished,
    // other requests can use their spot (general queue concurrency increased to 6)
    if (reservedRequests.includes(fn)) {
      return reservedQueue.add(fetchFn).finally(() => {
        // When a reserved request finishes, allow other requests to use its spot
        generalQueue.setConcurrency(6);
        generalQueue.next();
      });
    } else {
      return generalQueue.add(fetchFn);
    }
  };

  /* Firebase functions - if you add one here, also add it to firebase.json! */

  // admin
  const setCustomClaim = async (email, type) => {
    const { result } = await callFn("admin-setCustomClaim", {
      email: email,
      type: type,
    });
    return result;
  };

  const removeCustomClaim = async (email, type) => {
    const { result } = await callFn("admin-removeCustomClaim", {
      email: email,
      type: type,
    });
    return result;
  };

  const resetPass = async (email, password, suppliedKey) => {
    const { result } = await callFn("admin-webPassReset", {
      email,
      password,
      suppliedKey,
    });
    return result;
  };

  // campaigns
  const getUserCampaigns = async (userId, teamIds) => {
    const { result } = await callFn("campaigns-getUserCampaigns", {
      userId: userId,
      teamIds: teamIds,
    });
    return result;
  };

  const getAdminCampaigns = async (teamIds) => {
    const { result } = await callFn("campaigns-getAdminCampaigns", {
      teamIds: teamIds,
    });
    return result;
  };

  const processCampaign = async (campaignInfo, paymentInfo) => {
    const { result } = await callFn("campaigns-processCampaign", {
      values: campaignInfo,
      payInfo: paymentInfo,
    });
    return result;
  };

  const createCampaign = async (values) => {
    const { result } = await callFn("campaigns-createCampaign", {
      values,
    });
    return result;
  };

  // campaignDrafts
  const createCampaignDraft = async (values) => {
    const { result } = await callFn("campaignDrafts-createCampaignDraft", {
      values,
    });
    return result;
  };

  const getCampaignDraftById = async (draftId) => {
    const { result } = await callFn("campaignDrafts-getCampaignDraftById", {
      draftId,
    });
    return result;
  };
  const getCampaignDraftsByTeams = async (ids) => {
    const { result } = await callFn("campaignDrafts-getCampaignDraftsByTeams", {
      teamIds: ids,
    });
    return result;
  };

  const editCampaignDraft = async (values) => {
    const { result } = await callFn("campaignDrafts-editCampaignDraft", {
      values,
    });
    return result;
  };

  const editCampaign = async (values) => {
    const { result } = await callFn("campaigns-editCampaign", {
      values,
    });
    return result;
  };

  const removeCampaignDraft = async (draftId) => {
    const { result } = await callFn("campaignDrafts-removeCampaignDraft", {
      draftId,
    });
    return result;
  };

  // companies
  const getAllCompaniesData = async () => {
    const { result } = await callFn("companies-getAllCompaniesData", {});
    return result;
  };

  const getCompanyById = async (id) => {
    const { result } = await callFn("companies-getCompanyById", { id });
    return result;
  };

  // companyGroups
  const createCompAndGroups = async ({ company, teams }) => {
    const { result } = await callFn("companyGroups-createCompAndGroups", {
      company,
      teams,
    });
    return result;
  };

  // competitions
  const createCompetition = async ({
    title,
    startDate,
    endDate,
    parentId,
    description,
    iteration,
    sponsors,
    prizeId,
    numWinners,
  }) => {
    const { result } = await callFn("competitions-createCompetition", {
      title,
      startDate,
      endDate,
      parentId,
      description,
      iteration,
      sponsors,
      prizeId,
      numWinners,
    });
    return result;
  };

  const getCurrentIteration = async (parentId) => {
    const { result } = await callFn("competitions-getCurrentIteration", {
      parentId,
    });
    return result;
  };

  const getFutureIterations = async (parentId) => {
    const { result } = await callFn("competitions-getFutureIterations", {
      parentId,
    });
    return result;
  };
  const getAllCompetitions = async () => {
    const { result } = await callFn("competitions-getActiveCompetitions");
    return result;
  };
  const getActiveParents = async () => {
    const { result } = await callFn("competitionParents-getActiveParents");
    return result;
  };
  const getCompetitionByCompany = async (uidCompany) => {
    const { result } = await callFn("competitions-getCompetitionByCompany", {
      uidCompany,
    });
    return result;
  };

  const disableCompetition = async (uidCompetition) => {
    const { result } = await callFn("competitions-disableCompetition", {
      uidCompetition,
    });
    return result;
  };

  const getActiveCompetitions = async () => {
    const { result } = await callFn("competitions-getActiveCompetitions", {});
    return result;
  };

  const getActiveCompsByParents = async (ids) => {
    const { result } = await callFn("competitions-getActiveCompsByParents", {
      ids,
    });
    return result;
  };

  const updateCompetition = async (competition) => {
    const { result } = await callFn("competitions-updateCompetition", {
      competition,
    });
    return result;
  };

  // competitionGroups
  const addGroupsToCompetition = async (parentId, groups) => {
    const { result } = await callFn(
      "competitionGroups-addGroupsToCompetition",
      { parentId, groups },
    );
    return result;
  };

  // profiles
  const getProfileByEmail = async (email) => {
    const { result } = await callFn("profiles-getProfileByEmail", {
      email,
    });
    return result;
  };

  // teams
  const createCodes = async (teams) => {
    const { result } = await callFn("teams-createCodes", teams);
    return result;
  };

  const disableCode = async (code) => {
    const { result } = await callFn("teams-disableCode", { code });
    return result;
  };

  const getTierGroupsByCompany = async (uidCompany) => {
    const { result } = await callFn("teams-getTierGroupsByCompany", {
      uidCompany,
    });
    return result;
  };

  const getTierGroupsByCompParent = async (parentCompId) => {
    const { result } = await callFn("tierGroups-getTierGroupsByCompParent", {
      parentCompId,
    });
    return result;
  };

  const getTierGroupsByTier = async (tierId) => {
    const { result } = await callFn("tierGroups-getTierGroupsByTier", {
      tierId,
    });
    return result;
  };

  const updateGroup = async (group) => {
    const { result } = await callFn("tierGroups-updateGroup", { group });
    return result;
  };

  const getTiersByCompany = async (uidCompany) => {
    const { result } = await callFn("teams-getTiersByCompany", { uidCompany });
    return result;
  };

  const getTiersByCompetition = async (parentCompId) => {
    const { result } = await callFn("tiers-getTiersByCompetition", {
      parentCompId,
    });
    return result;
  };

  const addCompetitionToTiers = async (tiers, parentCompId) => {
    const { result } = await callFn("tiers-addCompetitionToTiers", {
      tiers,
      parentCompId,
    });
    return result;
  };

  const listAllUserTeams = async (id) => {
    const { result } = await callFn("teams-listAllUserTeams", {
      userId: id,
    });
    return result;
  };

  const getProfileAndTeamsById = async () => {
    const { result } = await callFn("teams-getProfileAndTeamsById");
    return result;
  };

  const getCompetitionsByUser = async (email) => {
    const { result } = await callFn("userCompetitions-getCompetitionsByUser", {
      email,
    });
    return result;
  };

  // tlcTryps
  const getAverages = async (userId) => {
    const { result } = await callFn("tlcTryps-getAverages", { userId });
    return result;
  };

  const getCompanyGroupScores = async (groups, week) => {
    const { result } = await callFn("tlcTryps-getCompanyGroupScores", {
      groups,
      week,
    });
    return result;
  };

  const getGroupRank = async (week, userId) => {
    const { result } = await callFn("tlcTryps-getGroupRank", {
      week,
      userId,
    });
    return result;
  };

  const getUserScoresInRange = async (weekMin, weekMax, userId) => {
    const { result } = await callFn("tlcTryps-getUserScoresInRange", {
      weekMin,
      weekMax,
      userId,
    });
    return result;
  };

  const getTopTryps = async (userId, resultCount) => {
    const { result } = await callFn("tlcTryps-getTopTrips", {
      userId,
      resultCount,
    });
    return result;
  };

  const getLowestWeekStat = async (userId, week) => {
    const { result } = await callFn("tlcTryps-getLowestWeekScore", {
      userId,
      week,
    });
    return result;
  };

  // tlRequests
  const getTrypMap = async (id) => {
    const { result } = await callFn("tlRequests-getTrypMap", { userId: id });
    return result;
  };

  // tlcVideos
  const addVideo = async (video) => {
    const { result } = await callFn("tlcVideos-addVideo", { video });
    return result;
  };

  const deleteVideo = async (video) => {
    const { result } = await callFn("tlcVideos-deleteVideo", { video });
    return result;
  };

  const getAllVideos = async () => {
    const { result } = await callFn("tlcVideos-getAllVideos", {});
    return result;
  };

  const updateVideo = async (video) => {
    const { result } = await callFn("tlcVideos-updateVideo", { video });
    return result;
  };

  const getFeaturedVideo = async () => {
    const { result } = await callFn("tlcVideos-getFeaturedVideo", {});
    return result;
  };

  // tlcWatchLists
  const getWatchListByUserId = async (userId) => {
    const { result } = await callFn("tlcWatchLists-getWatchListByUserId", {
      userId,
    });
    return result;
  };

  const updateWatchList = async (watchList) => {
    const { result } = await callFn("tlcWatchLists-updateWatchList", {
      watchList,
    });
    return result;
  };

  const getLeaderboardForCompetition = async (
    parentCompId,
    week,
    isMedidas,
    userId,
  ) => {
    const { result } = await callFn(
      "competitionGroups-getLeaderboardForCompetition",
      {
        parentCompId,
        week,
        isMedidas,
        userId,
      },
    );
    return result;
  };

  const getTeamsOnACompetition = async (teamIds) => {
    const { result } = await callFn(
      "competitionGroups-getTeamsOnACompetition",
      {
        teamIds,
      },
    );
    return result;
  };

  const getUserCompetitionScores = async (userId, week) => {
    const { result } = await callFn(
      "competitionGroups-getUserCompetitionScores",
      {
        userId,
        week,
      },
    );
    return result;
  };

  const createCompParent = async (title, uidCompany) => {
    const { result } = await callFn("competitionParents-createCompParent", {
      title,
      uidCompany,
    });
    return result;
  };

  const getCompParentById = async (id) => {
    const { result } = await callFn("competitionParents-getCompParentById", {
      id,
    });
    return result;
  };

  const getCompParentsByCompany = async (uidCompany) => {
    const { result } = await callFn(
      "competitionParents-getCompParentsByCompany",
      {
        uidCompany,
      },
    );
    return result;
  };

  const getCompParentsByIdArray = async (ids) => {
    const { result } = await callFn(
      "competitionParents-getCompParentsByIdArray",
      {
        ids,
      },
    );
    return result;
  };

  const updateCompParent = async (parent) => {
    const { result } = await callFn("competitionParents-updateCompParent", {
      parent,
    });
    return result;
  };

  const batchCreateCompBanners = async (compBanners) => {
    const { result } = await callFn(
      "competitionBanners-batchCreateCompBanners",
      {
        compBanners,
      },
    );
    return result;
  };

  const getCompBannerByCompId = async (compId) => {
    const { result } = await callFn(
      "competitionBanners-getCompBannerByCompId",
      {
        compId,
      },
    );
    return result;
  };

  const getPrizeTierById = async (id) => {
    const { result } = await callFn("prizeTiers-getPrizeTierById", {
      id,
    });
    return result;
  };

  const getUsersInCompetition = async (parentId) => {
    const { result } = await callFn("userCompetitions-getUsersInCompetition", {
      parentId,
    });
    return result;
  };

  const getActiveCompsByUser = async (isMedidas) => {
    const { result } = await callFn("userCompetitions-getActiveCompsByUser", {
      isMedidas,
    });
    return result;
  };

  const getPrizeTiersByType = async (id) => {
    const { result } = await callFn("prizeTiers-getPrizeTiersByType", {
      id,
    });
    return result;
  };

  const getPrizeTiersByMultipleIds = async (ids) => {
    const { result } = await callFn("prizeTiers-getPrizeTiersByMultipleIds", {
      ids,
    });
    return result;
  };

  const getAllPrizeTypes = async () => {
    const { result } = await callFn("prizeTypes-getAllPrizeTypes", {});
    return result;
  };

  const getWinnersByCompetition = async (competitionId, parentCompId) => {
    const { result } = await callFn("campaigns-getWinnersByCompetition", {
      competitionId,
      parentCompId,
    });
    return result;
  };

  const getTotalUsersInComp = async (parentId) => {
    const { result } = await callFn("userCompetitions-getTotalUsersInComp", {
      parentId,
    });
    return result;
  };

  const getActivesAndGPCampaign = async (userId, competitionId) => {
    const { result } = await callFn("campaigns-getActivesAndGPCampaign", {
      userId,
      competitionId,
    });
    return result;
  };

  const getCompaniesByIds = async (ids) => {
    const { result } = await callFn("companies-getCompaniesByIds", {
      ids,
    });
    return result;
  };

  const getConstant = async (name) => {
    const { result } = await callFn("constants-getConstant", {
      name,
    });
    return result;
  };

  const adminGetUserByEmail = async (email) => {
    const { result } = await callFn("admin-getUserByEmail", { email });
    return result;
  };

  const getSRCChallengeInfo = async (parentId, competitionId, userId) => {
    const { result } = await callFn(
      "competitionCampaigns-getSRCChallengeInfo",
      {
        parentId,
        competitionId,
        userId,
      },
    );
    return result;
  };

  const getCorporateWinnersByUser = async (userId) => {
    const { result } = await callFn("campaigns-getCorporateWinnersByUser", {
      userId,
    });
    return result;
  };

  const getCorporateCampaignsByUser = async (userId) => {
    const { result } = await callFn("campaigns-getCorporateCampaignsByUser", {
      userId,
    });
    return result;
  };

  return {
    firebase,
    getFirebase,
    doSignInWithEmailAndPassword,
    doSignOut,
    getAllCompaniesData,
    setCustomClaim,
    removeCustomClaim,
    getUserCampaigns,
    getAdminCampaigns,
    processCampaign,
    createCampaign,
    createCampaignDraft,
    getCampaignDraftById,
    getCampaignDraftsByTeams,
    removeCampaignDraft,
    editCampaignDraft,
    editCampaign,
    getAllVideos,
    addVideo,
    updateVideo,
    deleteVideo,
    getAverages,
    getCompanyGroupScores,
    getGroupRank,
    getUserScoresInRange,
    listAllUserTeams,
    getProfileAndTeamsById,
    getTopTryps,
    getTrypMap,
    getLowestWeekStat,
    signInWithGooglePopUp,
    signInWithGoogleRedirect,
    resetPass,
    getWatchListByUserId,
    updateWatchList,
    getCompanyById,
    signInWithCustomToken,
    getActiveParents,
    createCodes,
    disableCode,
    createCompetition,
    getCurrentIteration,
    getUserCompetitionScores,
    getFutureIterations,
    getCompetitionByCompany,
    createCompAndGroups,
    disableCompetition,
    getActiveCompetitions,
    getActiveCompsByParents,
    updateCompetition,
    addGroupsToCompetition,
    getTierGroupsByCompany,
    getTierGroupsByCompParent,
    getTierGroupsByTier,
    getTiersByCompany,
    getTiersByCompetition,
    updateGroup,
    addCompetitionToTiers,
    getCompetitionsByUser,
    getLeaderboardForCompetition,
    getTeamsOnACompetition,
    getProfileByEmail,
    createCompParent,
    getCompParentById,
    getCompParentsByCompany,
    getCompParentsByIdArray,
    updateCompParent,
    batchCreateCompBanners,
    getFeaturedVideo,
    getAllCompetitions,
    getUsersInCompetition,
    getActiveCompsByUser,
    getPrizeTierById,
    getPrizeTiersByType,
    getPrizeTiersByMultipleIds,
    getAllPrizeTypes,
    getWinnersByCompetition,
    getCompBannerByCompId,
    getTotalUsersInComp,
    getActivesAndGPCampaign,
    getCompaniesByIds,
    getConstant,
    adminGetUserByEmail,
    getSRCChallengeInfo,
    getCorporateWinnersByUser,
    getCorporateCampaignsByUser,
  };
  // Remember to add your function to corresponding firebase.json file(s) as well!!
  // And maybe restart your pc because of weird firebase caching
};
